import {
  type ChangeEvent,
  Component,
  type ComponentProps,
  createRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { Button as BlueprintButton, InputGroup, Spinner } from '@blueprintjs/core';
import { type DateRange, DateRangePicker } from '@blueprintjs/datetime';
import { ActivityResourceFiltersOptions } from '@gonfalon/change-history/ResourceFilters/ActivityResourceFilters';
import {
  Button,
  Dialog,
  DialogTrigger,
  Group,
  Heading,
  IconButton,
  Input,
  ListBox,
  ListBoxItem,
  Modal,
  ModalOverlay,
  Popover,
  SearchField,
  Select,
  Tooltip,
  TooltipTrigger,
} from '@launchpad-ui/components';
import { Icon as LaunchpadIcon } from '@launchpad-ui/icons';
import { endOfDay, format, subMonths } from 'date-fns';

import { CircularIconButton, type RequestError } from '../../../shared';
import { Box, Flex } from '../../../shared/components';
import { useSettings } from '../../../shared/hooks/useSettings';
import { buildAuditLogEntryDeepLink } from '../../auditLogLinks';
import {
  type Account,
  type AuditLogEntries,
  type AuditLogEntryDetail,
  getMemberDisplayName,
  type Member,
  NO_RESOURCE_SELECTED,
  ResourcesReadableNames,
  ResourcesWrittenExpressions,
  type ResourceType,
} from '../../model';

import { ActionFilter } from './ActionFilter';
import { AuditLogEntryDetails } from './AuditLogEntryDetails';
import { AuditLogEntryOverview } from './AuditLogEntryOverview';
import { AuditLogExport } from './AuditLogExport';
import { CopyLinkButton } from './CopyLinkButton';

import './styles.scss';

function AuditLogExportWhenFlagged(props: ComponentProps<typeof AuditLogExport>) {
  const { enabledAuditlogJsonExport } = useSettings();
  if (!enabledAuditlogJsonExport) {
    return null;
  }
  return <AuditLogExport {...props} />;
}

function AuditLogEntryOverviewFlagged(props: ComponentProps<typeof AuditLogEntryOverview>) {
  const { enableAuditLogActorDisplay } = useSettings();
  return <AuditLogEntryOverview {...props} enableActorDisplay={Boolean(enableAuditLogActorDisplay)} />;
}

function AuditLogEntryDetailsFlagged(props: ComponentProps<typeof AuditLogEntryDetails>) {
  const { enableUpdatedRoleDiffs, enableAuditLogActorDisplay } = useSettings();
  return (
    <AuditLogEntryDetails
      {...props}
      enableUpdatedRoleDiffs={enableUpdatedRoleDiffs}
      enableActorDisplay={Boolean(enableAuditLogActorDisplay)}
    />
  );
}

// Resource types from the shared ActivityResourceFiltersOptions (driven by feature flags)
// Plus all additional resource types available in vitalstatistix that have audit log support
//
// Note: The following resource types do NOT have audit log support yet and are excluded:
// - ai-evaluation: AI evaluation configs (no CreateEntryForAIEvaluation)
// - domain-verification: SSO domain verification (analytics only, no audit logs)
// - issue: Error monitoring issues (no CreateEntryForIssue)
// - metric-data-source: Metric data connections (no CreateEntryForMetricDataSource)
// - product-analytics-dashboard: Product analytics dashboards (no CreateEntryForProductAnalyticsDashboard)
// - error, log, session, trace, vega: Observability data types (not configuration resources)
//
// Deprecated resource types (excluded):
// - goal: Legacy name for metrics (replaced by 'metric' resource type)
// - user: Deprecated users API (replaced by contexts API; DisableUsersApiEndpoints flag)
const RESOURCE_TYPES = Array.from(
  new Set([
    NO_RESOURCE_SELECTED,
    ...ActivityResourceFiltersOptions.map((option) => option.value as ResourceType),
    // Additional resource types for vitalstatistix audit log (configuration resources only)
    'ai-tool' as ResourceType,
    'alert' as ResourceType, // Observability alerts
    'destination' as ResourceType, // Data export destinations
    'observability-dashboard' as ResourceType, // Observability dashboards
    'observability-settings' as ResourceType, // Observability settings
    'pending-request' as ResourceType, // Administrative requests
    'token' as ResourceType, // Personal access tokens
    'version' as ResourceType, // Application versions
  ]),
).sort((a, b) => {
  // Keep NO_RESOURCE_SELECTED at the top
  if (a === NO_RESOURCE_SELECTED) {
    return -1;
  }
  if (b === NO_RESOURCE_SELECTED) {
    return 1;
  }
  // Sort everything else alphabetically by display name
  const nameA = ResourcesReadableNames[a] ?? a;
  const nameB = ResourcesReadableNames[b] ?? b;
  return nameA.localeCompare(nameB);
});

const ResourceSelectTooltip = () => (
  <div>
    <p>Selecting a resource type will pre-populate the resource specifier with a sensible default.</p>
    <p>The asterisks can be replaced with specific resource names to filter.</p>
    <p>{'For example, proj/default:env/test:flag/* returns all flags in the test environment.'}</p>
  </div>
);

const QueryTooltip = () => (
  <div>
    <p>Search for this string within resources matched by the resource specifier.</p>
    <p>For example, querying “Manhattan” on flag resources will match names and descriptions.</p>
  </div>
);

type StateProps = Readonly<{
  auditLogEntries: AuditLogEntries;
  entryDetails?: AuditLogEntryDetail | null;
  account: Account;
  // Current search parameters to keep form in sync
  currentQuery?: string;
  currentSpec?: string;
  currentMember?: string;
  currentFuzzy?: boolean;
  currentDate?: ReadonlyArray<Date | null>;
  currentActions?: readonly string[];
  enableVitalAuditLogEnhancedSearch?: boolean;
  members?: Member[];
  membersLoading?: boolean;
  onSelectEntry: (entryId: string) => void;
  onCloseEntryDetails: () => void;
  selectedEntryId?: string | null;
  selectedEntryUrl?: string | null;
  enableAuditLogEntryLinks?: boolean;
  onBackToNewest?: () => void;
  entryDetailsError?: RequestError | null;
  onSearchParamsChange: ({
    query,
    spec,
    member,
    date,
    actions,
  }: {
    query?: string;
    spec?: string;
    member?: string;
    fuzzy?: boolean;
    date?: ReadonlyArray<Date | null | number>;
    actions?: readonly string[];
  }) => void;
}>;

type State = {
  query: string;
  spec: string;
  member: string;
  actions: string[];
  date: ReadonlyArray<Date | null | number>;
  dateSelected: DateRange;
  resourceSelected: ResourceType;
  memberSearch: string;
  isMemberSelectorOpen: boolean;
  searchMode: 'simple' | 'advanced';
  isOpen: boolean;
  highlightedEntryId: string | null;
  hideLoadNewerButton: boolean;
  isApplyingNewer: boolean;
  newerFailed: boolean;
};

export class AuditLog extends Component<StateProps, State> {
  public state: State = this.getInitialState();

  private isEnhancedSearchEnabled() {
    return this.props.enableVitalAuditLogEnhancedSearch !== false;
  }

  private getSearchModeForState({
    currentFuzzy,
    hasAdvancedOnlySpec,
  }: {
    currentFuzzy?: boolean;
    hasAdvancedOnlySpec: boolean;
  }): 'simple' | 'advanced' {
    if (currentFuzzy === false || hasAdvancedOnlySpec) {
      return 'advanced';
    }

    return 'simple';
  }

  private getResourceTypeForSpec(spec?: string): ResourceType {
    if (!spec) {
      return NO_RESOURCE_SELECTED;
    }

    const matchingResourceType = RESOURCE_TYPES.find((type) => (ResourcesWrittenExpressions[type] ?? '') === spec);
    return matchingResourceType ?? NO_RESOURCE_SELECTED;
  }

  private getInitialState(): State {
    const {
      currentQuery = '',
      currentSpec = '',
      currentMember = '',
      currentFuzzy,
      currentDate = [null, null],
      currentActions = [],
    } = this.props;
    const isEnhancedSearchEnabled = this.isEnhancedSearchEnabled();
    const resourceSelected = this.getResourceTypeForSpec(currentSpec);
    const hasAdvancedOnlySpec = Boolean(currentSpec) && resourceSelected === NO_RESOURCE_SELECTED;
    const searchMode = isEnhancedSearchEnabled
      ? this.getSearchModeForState({ currentFuzzy, hasAdvancedOnlySpec })
      : 'advanced';

    return {
      query: currentQuery,
      spec: currentSpec,
      member: isEnhancedSearchEnabled ? currentMember : '',
      actions: isEnhancedSearchEnabled ? [...currentActions] : [],
      date: currentDate,
      dateSelected: this.getDateSelectedFromDate(currentDate),
      resourceSelected,
      memberSearch: '',
      isMemberSelectorOpen: false,
      searchMode,
      isOpen: false,
      highlightedEntryId: null,
      hideLoadNewerButton: false,
      isApplyingNewer: false,
      newerFailed: false,
    };
  }

  private getDateSelectedFromDate(date: ReadonlyArray<Date | null | number>): DateRange {
    if (date && date[0] && date[1]) {
      return [new Date(date[0]), new Date(date[1])];
    }
    return [null, null];
  }

  private loadMoreTrigger = createRef<HTMLDivElement>();

  private intersectionObserver?: IntersectionObserver;

  private loadMoreTimeoutId?: number;

  private observerRetryTimeoutId?: number;

  // Tracks the last entry we scrolled to so a deep-link only scrolls once per
  // selection (and re-scrolls if the modal is closed and re-opened).
  private lastScrolledEntryId?: string | null;

  // Entries on screen when the search last changed. Once that request settles,
  // the open row can scroll into the new window and Load newer can show again.
  private entriesBeforeSearch?: StateProps['auditLogEntries']['entries'];

  private highlightTimeoutId?: number;

  // Snapshot taken just before newer entries are inserted, so the row on
  // screen stays put whether the new rows land above or below it.
  private prependScrollSnapshot?: { y: number; anchorId?: string; anchorTop?: number };

  private revealingNewer = false;

  public componentDidMount() {
    this.setupInfiniteScroll();
    this.scrollSelectedEntryIntoView();
  }

  public componentDidUpdate(prevProps: StateProps, _prevState: State) {
    this.keepPrependedRowsInPlace(prevProps);
    if (prevProps.selectedEntryId !== this.props.selectedEntryId && this.props.selectedEntryId) {
      this.setState({ hideLoadNewerButton: false, isApplyingNewer: false, newerFailed: false });
    }
    if (this.searchWindowChanged(prevProps)) {
      this.entriesBeforeSearch = prevProps.auditLogEntries.entries;
      if (this.state.hideLoadNewerButton || this.state.isApplyingNewer || this.state.newerFailed) {
        this.setState({ hideLoadNewerButton: false, isApplyingNewer: false, newerFailed: false });
      }
    }
    if (
      this.entriesBeforeSearch &&
      !this.props.auditLogEntries.isFetching &&
      !this.props.auditLogEntries.isPlaceholderData &&
      (this.props.auditLogEntries.entries !== this.entriesBeforeSearch || prevProps.auditLogEntries.isFetching)
    ) {
      this.entriesBeforeSearch = undefined;
      this.lastScrolledEntryId = undefined;
    }
    // Bring a deep-linked entry into view once its row is rendered (entries may
    // still be loading when the link is first opened).
    this.scrollSelectedEntryIntoView();

    // Sync form state when search parameters change from parent
    const {
      currentQuery,
      currentSpec,
      currentMember,
      currentFuzzy,
      currentDate,
      currentActions,
      enableVitalAuditLogEnhancedSearch,
    } = this.props;
    const {
      currentQuery: prevQuery,
      currentSpec: prevSpec,
      currentMember: prevMember,
      currentFuzzy: prevFuzzy,
      currentDate: prevDate,
      currentActions: prevActions,
      enableVitalAuditLogEnhancedSearch: prevEnableVitalAuditLogEnhancedSearch,
    } = prevProps;

    // Only update state if props actually changed AND state doesn't already match
    const propsChanged =
      currentQuery !== prevQuery ||
      currentSpec !== prevSpec ||
      currentMember !== prevMember ||
      currentFuzzy !== prevFuzzy ||
      JSON.stringify(currentDate) !== JSON.stringify(prevDate) ||
      JSON.stringify(currentActions) !== JSON.stringify(prevActions) ||
      enableVitalAuditLogEnhancedSearch !== prevEnableVitalAuditLogEnhancedSearch;

    const nextActions = this.isEnhancedSearchEnabled() ? [...(currentActions ?? [])] : [];
    const stateNeedsUpdate =
      this.state.query !== (currentQuery || '') ||
      this.state.spec !== (currentSpec || '') ||
      this.state.member !== (this.isEnhancedSearchEnabled() ? currentMember || '' : '') ||
      JSON.stringify(this.state.date) !== JSON.stringify(currentDate || [null, null]) ||
      JSON.stringify(this.state.actions) !== JSON.stringify(nextActions);

    if (propsChanged && stateNeedsUpdate) {
      const isEnhancedSearchEnabled = this.isEnhancedSearchEnabled();
      const resourceSelected = this.getResourceTypeForSpec(currentSpec);
      const hasAdvancedOnlySpec = Boolean(currentSpec) && resourceSelected === NO_RESOURCE_SELECTED;
      const nextSearchMode = isEnhancedSearchEnabled
        ? this.getSearchModeForState({ currentFuzzy, hasAdvancedOnlySpec })
        : 'advanced';

      this.setState({
        query: currentQuery || '',
        spec: currentSpec || '',
        member: isEnhancedSearchEnabled ? currentMember || '' : '',
        actions: nextActions,
        date: currentDate || [null, null],
        dateSelected: this.getDateSelectedFromDate(currentDate || [null, null]),
        resourceSelected,
        searchMode: nextSearchMode,
      });
      return; // Early return to avoid observer setup during state update
    }

    // Re-observe the trigger element after updates (only if no state change is happening)
    this.observeLoadMoreTrigger();
  }

  public componentWillUnmount() {
    this.cleanupInfiniteScroll();
    if (this.highlightTimeoutId) {
      clearTimeout(this.highlightTimeoutId);
      this.highlightTimeoutId = undefined;
    }
  }

  private searchWindowChanged = (prevProps: StateProps) =>
    prevProps.currentQuery !== this.props.currentQuery ||
    prevProps.currentSpec !== this.props.currentSpec ||
    prevProps.currentMember !== this.props.currentMember ||
    prevProps.currentFuzzy !== this.props.currentFuzzy ||
    JSON.stringify(prevProps.currentDate) !== JSON.stringify(this.props.currentDate) ||
    JSON.stringify(prevProps.currentActions) !== JSON.stringify(this.props.currentActions);

  // Scroll a selected entry into view and briefly highlight it when its row is
  // outside the viewport (a deep link, or a row reached by paging). A row that
  // is already on screen is left alone. No-ops when nothing is selected or the
  // row isn't rendered yet.
  private scrollSelectedEntryIntoView = () => {
    const { selectedEntryId } = this.props;

    if (!selectedEntryId) {
      this.cancelDeepLinkScroll();
      return;
    }

    if (this.lastScrolledEntryId === selectedEntryId) {
      return;
    }

    const row = document.getElementById(`audit-entry-${selectedEntryId}`);
    if (!row) {
      // The row is not on the loaded page yet. The container asks the list API
      // for the page that contains this entry; this runs again once it renders.
      return;
    }

    this.lastScrolledEntryId = selectedEntryId;
    // A click on a row that's already on screen shouldn't move the list.
    // Deep links and off-page entries still scroll once the row is outside the viewport.
    if (!this.isRowOutsideViewport(row)) {
      return;
    }

    row.scrollIntoView({ behavior: 'auto', block: 'center' });

    this.setState({ highlightedEntryId: selectedEntryId });
    if (this.highlightTimeoutId) {
      clearTimeout(this.highlightTimeoutId);
    }
    this.highlightTimeoutId = window.setTimeout(() => {
      this.setState({ highlightedEntryId: null });
      this.highlightTimeoutId = undefined;
    }, 2000);
  };

  private isRowOutsideViewport = (row: HTMLElement): boolean => {
    const { top, bottom } = row.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    return bottom <= 0 || top >= viewportHeight;
  };

  // Closing the modal leaves the list where it is. Only the highlight is cleared.
  private cancelDeepLinkScroll = () => {
    this.lastScrolledEntryId = null;
    if (this.highlightTimeoutId) {
      clearTimeout(this.highlightTimeoutId);
      this.highlightTimeoutId = undefined;
    }
    if (this.state.highlightedEntryId) {
      this.setState({ highlightedEntryId: null });
    }
  };

  private setupInfiniteScroll = () => {
    // Set up intersection observer for infinite scroll
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;

        // Clear any pending timeout
        if (this.loadMoreTimeoutId) {
          clearTimeout(this.loadMoreTimeoutId);
        }

        // When the trigger element comes into view and we're not already loading
        if (
          entry.isIntersecting &&
          !this.revealingNewer &&
          !this.props.auditLogEntries.isFetching &&
          this.props.auditLogEntries.loadMore
        ) {
          // Debounce to handle slow scrolling better
          this.loadMoreTimeoutId = window.setTimeout(() => {
            // Double-check conditions at execution time. A "Load newer" walk can
            // take long enough for this trigger to fire; that append is not the
            // insert the scroll snapshot is waiting for.
            if (!this.revealingNewer && !this.props.auditLogEntries.isFetching && this.props.auditLogEntries.loadMore) {
              this.getMoreEntries();
            }
            this.loadMoreTimeoutId = undefined;
          }, 100); // 100ms debounce
        }
      },
      {
        // Trigger when element is 200px from being visible - more sensitive
        rootMargin: '200px',
        // Multiple thresholds for better slow scroll detection
        threshold: [0, 0.1, 0.5, 1.0],
      },
    );

    // Start observing once the component mounts
    this.observeLoadMoreTrigger();
  };

  private observeLoadMoreTrigger = () => {
    if (this.intersectionObserver && this.loadMoreTrigger.current) {
      // Disconnect first to avoid duplicate observers
      this.intersectionObserver.disconnect();
      this.intersectionObserver.observe(this.loadMoreTrigger.current);
    } else if (this.intersectionObserver && !this.loadMoreTrigger.current) {
      // Only retry if there should be more entries to load (element should exist)
      const shouldElementExist = this.props.auditLogEntries._links.next;

      if (shouldElementExist) {
        // Clear any existing retry timeout
        if (this.observerRetryTimeoutId) {
          clearTimeout(this.observerRetryTimeoutId);
        }

        // Retry after a short delay if element should exist but isn't ready yet
        this.observerRetryTimeoutId = window.setTimeout(() => {
          this.observeLoadMoreTrigger();
          this.observerRetryTimeoutId = undefined;
        }, 100);
      }
      // If element shouldn't exist, don't retry - this prevents infinite loops
    }
  };

  private cleanupInfiniteScroll = () => {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = undefined;
    }
    if (this.loadMoreTimeoutId) {
      clearTimeout(this.loadMoreTimeoutId);
      this.loadMoreTimeoutId = undefined;
    }
    if (this.observerRetryTimeoutId) {
      clearTimeout(this.observerRetryTimeoutId);
      this.observerRetryTimeoutId = undefined;
    }
  };

  public render() {
    const {
      account,
      auditLogEntries,
      entryDetails,
      entryDetailsError,
      onSelectEntry,
      onCloseEntryDetails,
      selectedEntryId,
      selectedEntryUrl,
      enableAuditLogEntryLinks,
    } = this.props;
    const visibleEntries = auditLogEntries.entries
      .filter((entry) => entry && entry._id)
      .filter((entry, index, self) => self.findIndex((candidate) => candidate._id === entry._id) === index);
    const isEnhancedSearchEnabled = this.isEnhancedSearchEnabled();
    let searchForm = this.renderLegacySearchForm();

    if (isEnhancedSearchEnabled) {
      searchForm = this.state.searchMode === 'simple' ? this.renderSimpleSearchForm() : this.renderAdvancedSearchForm();
    }

    return (
      <>
        <Box className="AuditlogHead">
          <Box className="AuditLogEntry-form">{searchForm}</Box>
          <AuditLogExportWhenFlagged
            account={account}
            query={this.props.currentQuery || ''}
            spec={this.props.currentSpec || ''}
            member={this.props.currentMember || ''}
            fuzzy={this.props.currentFuzzy}
            date={this.props.currentDate || [null, null]}
          />
        </Box>
        <Box>
          {this.renderNewerControls()}
          {visibleEntries.length > 0 &&
            visibleEntries.map((entry) => (
              <Box
                key={entry._id}
                className={`AuditLogEntry${
                  this.state.highlightedEntryId === entry._id ? ' AuditLogEntry--highlighted' : ''
                }`}
                id={`audit-entry-${entry._id}`}
              >
                <Flex alignItems="flex-start">
                  <Box mr={2} className="AuditLogEntry-rowActions">
                    <TooltipTrigger>
                      <IconButton
                        icon="preview"
                        variant="minimal"
                        className="AuditLogEntryDetails-button"
                        aria-label="View Details"
                        onPress={() => onSelectEntry(entry._id)}
                      />
                      <Tooltip placement="top" variant="popover">
                        View Details
                      </Tooltip>
                    </TooltipTrigger>
                    {enableAuditLogEntryLinks && (
                      <CopyLinkButton
                        url={buildAuditLogEntryDeepLink(account._id, entry._id)}
                        ariaLabel="Copy link to this audit log entry"
                        className="AuditLogEntry-copyLinkButton"
                      />
                    )}
                  </Box>
                  <Box style={{ flex: '1' }}>
                    <AuditLogEntryOverviewFlagged entry={entry} account={account} />
                  </Box>
                </Flex>
              </Box>
            ))}
          {visibleEntries.length === 0 && auditLogEntries.isFetching && (
            <Box className="AuditLogEntry AuditLogEntry-emptyState">
              <Spinner size={25} />
            </Box>
          )}
          {visibleEntries.length === 0 && !auditLogEntries.isFetching && (
            <Box className="AuditLogEntry AuditLogEntry-emptyState">
              <div className="AuditLogEntry-emptyStateCard">
                <div className="AuditLogEntry-emptyStateTitle">No results to display</div>
                <div className="AuditLogEntry-emptyStateDescription">Try using a different filter or query.</div>
              </div>
            </Box>
          )}
          {auditLogEntries._links.next && (
            <div className="AuditLogEntry-loadMoreContainer">
              {auditLogEntries.isFetching ? (
                <div className="AuditLogEntry-loadingState">Loading more entries...</div>
              ) : (
                <Button
                  isDisabled={auditLogEntries.isFetching}
                  onPress={this.getMoreEntries}
                  variant="primary"
                  className="AuditLogEntry-button"
                >
                  Load More (or scroll down)
                </Button>
              )}
              {/* Invisible trigger element for infinite scroll */}
              <div ref={this.loadMoreTrigger} className="AuditLogEntry-infiniteScrollTrigger" />
            </div>
          )}
        </Box>
        {selectedEntryId && selectedEntryUrl && (
          <AuditLogEntryDetailsFlagged
            account={account}
            entryUrl={selectedEntryUrl}
            entryDetails={entryDetails}
            entryDetailsError={entryDetailsError}
            enableAuditLogEntryLinks={enableAuditLogEntryLinks}
            isOpen
            onOpenChange={(open: boolean) => {
              if (!open) {
                onCloseEntryDetails();
              }
            }}
          />
        )}
      </>
    );
  }

  private renderSimpleSearchForm = () => (
    <Box className="AuditLogEntry-simpleToolbar">
      <Box className="AuditLogEntry-simpleToolbarSearchRow">
        <Box className="AuditLogEntry-simpleToolbarSearchGroup">
          <InputGroup
            id="queryTextInput"
            type="search"
            className="AuditLogEntry-simpleToolbarSearch"
            leftIcon="search"
            placeholder="Search audit log entries"
            value={this.state.query}
            onChange={this.handleQueryInputChange}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                this.filterEntriesByQuery();
              }
            }}
            rightElement={
              this.state.query ? (
                <BlueprintButton minimal icon="cross" aria-label="Clear audit log search" onClick={this.clearQuery} />
              ) : undefined
            }
          />
          <Button
            variant="primary"
            className="AuditLogEntry-simpleToolbarSearchAction"
            onPress={() => this.filterEntriesByQuery()}
          >
            Search
          </Button>
        </Box>
        {this.renderSearchModeToggle()}
      </Box>
      <Box className="AuditLogEntry-simpleToolbarFiltersRow">
        {this.renderCompactResourceFilter()}
        {this.renderCompactActionFilter()}
        {this.renderCompactMemberFilter()}
        {this.renderCompactDateFilter()}
      </Box>
    </Box>
  );

  private renderLegacySearchForm = () => (
    <>
      <Box className="AuditLogEntry-inputGroup">
        <Box className="AuditLogEntry-formGroup">
          <label className="AuditLogEntry-label" htmlFor="resourceSpecifierSelect">
            Resource Type <span className="bp4-text-muted">(</span>
            <a
              href="https://launchdarkly.com/docs/home/members/role-resources#understanding-resource-types-and-scopes"
              target="_blank"
              rel="noopener noreferrer"
            >
              reference <LaunchpadIcon name="link" size="small" className="audit-log-info-icon" />
            </a>
            <span className="bp4-text-muted">)</span>
            <CircularIconButton
              iconName="info"
              intent="neutral"
              size="small"
              tooltip={<ResourceSelectTooltip />}
              tooltipPlacement="top"
              ariaLabel="Resource type information"
            />
          </label>
          <Box className="AuditLogEntry-search AuditLogEntry-resourceFilter" id="resourceSpecifierSelect">
            <Select
              value={this.state.resourceSelected}
              onChange={(key) => {
                this.handleResourceItemSelect(key as ResourceType);
              }}
              className="AuditLogEntry-resourceFilter__select"
              aria-label="Resource filter"
            >
              <Button>
                <LaunchpadIcon name="filter" slot="start-icon" />
                <span>{ResourcesReadableNames[this.state.resourceSelected] ?? this.state.resourceSelected}</span>
                <LaunchpadIcon name="chevron-down" slot="end-icon" />
              </Button>
              <Popover>
                <ListBox>
                  {RESOURCE_TYPES.map((resourceType) => (
                    <ListBoxItem key={resourceType} id={resourceType}>
                      {ResourcesReadableNames[resourceType] ?? resourceType}
                    </ListBoxItem>
                  ))}
                </ListBox>
              </Popover>
            </Select>
            {this.state.resourceSelected !== NO_RESOURCE_SELECTED && (
              <Button
                onPress={this.clearResourceFilter}
                variant="minimal"
                className="AuditLogEntry-resourceFilter__clear"
                aria-label="Clear resource filter"
              >
                <LaunchpadIcon name="cancel" />
              </Button>
            )}
          </Box>
        </Box>
        <Box className="AuditLogEntry-formGroup">
          <label className="AuditLogEntry-label" htmlFor="resourceSpecifierTextInput">
            Resource specifier <span className="bp4-text-muted">(</span>
            <a
              href="https://launchdarkly.com/docs/home/account/roles/role-resources#expand-reference-list-of-supported-resources-in-launchdarkly"
              target="_blank"
              rel="noopener noreferrer"
            >
              query param spec <LaunchpadIcon name="link" size="small" className="audit-log-info-icon" />
            </a>
            <span className="bp4-text-muted">)</span>
          </label>
          <InputGroup
            id="resourceSpecifierTextInput"
            type="search"
            className="AuditLogEntry-search"
            placeholder="search resources"
            onChange={this.handleSpecInputChange}
            value={this.state.spec}
          />
        </Box>
      </Box>
      <Box className="AuditLogEntry-inputGroup">
        <Box className="AuditLogEntry-formGroup">
          <label className="AuditLogEntry-label" htmlFor="queryTextInput">
            Query <span className="bp4-text-muted">(query param q)</span>
            <CircularIconButton
              iconName="info"
              intent="neutral"
              size="small"
              tooltip={<QueryTooltip />}
              tooltipPlacement="top"
              ariaLabel="Query information"
            />
          </label>
          <InputGroup
            id="queryTextInput"
            type="search"
            className="AuditLogEntry-search"
            leftIcon="search"
            placeholder="search entries"
            value={this.state.query}
            onChange={this.handleQueryInputChange}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                this.filterEntriesByQuery();
              }
            }}
            rightElement={
              this.state.query ? (
                <BlueprintButton minimal icon="cross" aria-label="Clear audit log search" onClick={this.clearQuery} />
              ) : undefined
            }
          />
        </Box>
        <Box className="AuditLogEntry-formGroup">
          <button className="AuditLogEntry-selectdate" type="button" onClick={this.openDatePicker}>
            {this.getLegacyDateRangeText()}
            <LaunchpadIcon name="chevron-down" size="small" />
          </button>
          {this.state.date[0] && (
            <button
              type="button"
              className="AuditLogEntry-legacyDateClear"
              aria-label="Clear date filter"
              onClick={this.clearDates}
            >
              <LaunchpadIcon name="cancel" size="small" />
            </button>
          )}

          <ModalOverlay isOpen={this.state.isOpen} onOpenChange={this.handleDateOverlayOpenChange}>
            <Modal>
              <Dialog>
                <Heading slot="title">Select Date Range</Heading>
                <div className="AuditLogEntry-datepicker-content">
                  <DateRangePicker
                    onChange={this.handleDateSelection}
                    allowSingleDayRange
                    initialMonth={subMonths(new Date(), 1)}
                    value={this.state.dateSelected}
                  />
                </div>
                <div className="AuditLogEntry-datepicker-buttons">
                  <button
                    type="button"
                    className="AuditLogEntry--datepickerbutton AuditLogEntry--datepickerbutton-cancel"
                    onClick={this.cancelDateSelection}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="AuditLogEntry--datepickerbutton AuditLogEntry--datepickerbutton-apply"
                    onClick={this.filterEntriesByDate}
                  >
                    Apply
                  </button>
                </div>
              </Dialog>
            </Modal>
          </ModalOverlay>
        </Box>
      </Box>
      <Box className="AuditLogEntry-searchButtonAndDocLinkInputGroup">
        <Box className="AuditLogEntry-searchButtonAndDocLink">
          <Button variant="primary" onPress={() => this.filterEntriesByQuery()} style={{ width: '100%' }}>
            Search
          </Button>
        </Box>
      </Box>
    </>
  );

  private handleInlineFilterClear = (event: ReactMouseEvent<HTMLElement>, clearFn: () => void) => {
    event.preventDefault();
    event.stopPropagation();
    clearFn();
  };

  private handleInlineFilterClearPointerDown = (event: ReactPointerEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  private stopNativeEventPropagation = (event: ReactMouseEvent<HTMLElement>) => {
    const nativeEvent = event.nativeEvent as MouseEvent & {
      stopImmediatePropagation?: () => void;
    };

    nativeEvent.stopImmediatePropagation?.();
  };

  private handleInlineFilterClearKeyDown = (event: ReactKeyboardEvent<HTMLElement>, clearFn: () => void) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    clearFn();
  };

  private renderCompactFilterEndIcon = ({
    isActive,
    clearLabel,
    clearFn,
  }: {
    isActive: boolean;
    clearLabel: string;
    clearFn: () => void;
  }) =>
    isActive ? (
      <span
        className="AuditLogEntry-simpleToolbarControlEndIcon AuditLogEntry-simpleToolbarControlEndIcon--clear"
        role="button"
        tabIndex={0}
        aria-label={clearLabel}
        onPointerDownCapture={this.handleInlineFilterClearPointerDown}
        onKeyDown={(event) => this.handleInlineFilterClearKeyDown(event, clearFn)}
        onClick={(event) => {
          this.stopNativeEventPropagation(event);
          this.handleInlineFilterClear(event, clearFn);
        }}
      >
        <LaunchpadIcon name="cancel" size="small" />
      </span>
    ) : (
      <LaunchpadIcon className="AuditLogEntry-simpleToolbarControlEndIcon" name="chevron-down" />
    );

  private renderAdvancedSearchForm = () => (
    <Box className="AuditLogEntry-simpleToolbar">
      <Box className="AuditLogEntry-simpleToolbarSearchRow">
        <Box className="AuditLogEntry-simpleToolbarSearchGroup">
          <InputGroup
            id="resourceSpecifierTextInput"
            type="search"
            className="AuditLogEntry-simpleToolbarSpecInput AuditLogEntry-simpleToolbarSpecInput--advanced"
            leftIcon="search"
            aria-label="Resource specifier"
            placeholder="Resource specifier (e.g. proj/*:env/*:flag/*)"
            onChange={this.handleSpecInputChange}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                this.filterEntriesByQuery();
              }
            }}
            value={this.state.spec}
            rightElement={
              this.state.spec ? (
                <BlueprintButton
                  minimal
                  icon="cross"
                  aria-label="Clear resource specifier"
                  onClick={() => {
                    this.setState({ spec: '' }, () => {
                      this.emitSearchParams({ overrideSpec: '' });
                    });
                  }}
                />
              ) : undefined
            }
          />
          <Button
            variant="primary"
            className="AuditLogEntry-simpleToolbarSearchAction"
            onPress={() => this.filterEntriesByQuery()}
          >
            Search
          </Button>
        </Box>
        {this.renderSearchModeToggle()}
      </Box>
      <Box className="AuditLogEntry-simpleToolbarFiltersRow AuditLogEntry-simpleToolbarFiltersRow--advanced">
        {this.renderCompactMemberFilter()}
        {this.renderCompactDateFilter()}
      </Box>
    </Box>
  );

  private renderSearchModeToggle = () => (
    <Box className="AuditLogEntry-searchModeToggle" role="tablist" aria-label="Audit log search mode">
      <Button
        variant="minimal"
        className={`AuditLogEntry-searchModeTab${
          this.state.searchMode === 'simple' ? ' AuditLogEntry-searchModeTab--active' : ''
        }`}
        onPress={() => this.setSearchMode('simple')}
        isDisabled={this.state.searchMode === 'simple'}
        aria-pressed={this.state.searchMode === 'simple'}
      >
        Simple search
      </Button>
      <Button
        variant="minimal"
        className={`AuditLogEntry-searchModeTab${
          this.state.searchMode === 'advanced' ? ' AuditLogEntry-searchModeTab--active' : ''
        }`}
        onPress={() => this.setSearchMode('advanced')}
        isDisabled={this.state.searchMode === 'advanced'}
        aria-pressed={this.state.searchMode === 'advanced'}
      >
        Advanced search
      </Button>
    </Box>
  );

  private renderCompactResourceFilter = () => (
    <Select
      value={this.state.resourceSelected}
      onChange={(key) => {
        this.handleResourceItemSelect(key as ResourceType);
      }}
      className="AuditLogEntry-simpleToolbarControl AuditLogEntry-simpleToolbarControl--resource"
      aria-label="Resource filter"
    >
      <Button>
        <span className="AuditLogEntry-simpleToolbarFilterIconWrap">
          <LaunchpadIcon name="filter-list" size="small" />
          {this.state.resourceSelected !== NO_RESOURCE_SELECTED && (
            <span className="AuditLogEntry-simpleToolbarFilterDot" />
          )}
        </span>
        <span className="AuditLogEntry-simpleToolbarControlLabel">Resource</span>
        {this.state.resourceSelected !== NO_RESOURCE_SELECTED ? (
          <span className="AuditLogEntry-simpleToolbarSelectedValueText">
            {ResourcesReadableNames[this.state.resourceSelected] ?? this.state.resourceSelected}
          </span>
        ) : (
          <span className="AuditLogEntry-simpleToolbarAnyValue">any</span>
        )}
        {this.renderCompactFilterEndIcon({
          isActive: this.state.resourceSelected !== NO_RESOURCE_SELECTED,
          clearLabel: 'Clear resource filter',
          clearFn: this.clearResourceFilter,
        })}
      </Button>
      <Popover>
        <ListBox>
          {RESOURCE_TYPES.map((resourceType) => (
            <ListBoxItem key={resourceType} id={resourceType}>
              {ResourcesReadableNames[resourceType] ?? resourceType}
            </ListBoxItem>
          ))}
        </ListBox>
      </Popover>
    </Select>
  );

  private renderCompactActionFilter = () => {
    const { resourceSelected } = this.state;
    const isDisabled = resourceSelected === NO_RESOURCE_SELECTED;
    return (
      <ActionFilter
        account={this.props.account}
        resourceType={isDisabled ? undefined : resourceSelected}
        resourceLabel={ResourcesReadableNames[resourceSelected] ?? resourceSelected}
        isDisabled={isDisabled}
        selectedActions={this.state.actions}
        onChange={this.handleActionsChange}
      />
    );
  };

  private handleActionsChange = (actions: string[]) => {
    this.setState({ actions }, () => this.emitSearchParams());
  };

  private renderCompactMemberFilter = () => {
    const selectedMember = this.getSelectedMember();
    const selectedLabel = selectedMember ? this.getMemberDisplayLabel(selectedMember) : 'any';
    const filteredMembers = this.getFilteredMembers();
    const members = this.props.members ?? [];
    const isLoadingMembers = Boolean(this.props.membersLoading && members.length === 0);

    return (
      <DialogTrigger>
        <Button
          aria-label="Member filter"
          className="AuditLogEntry-simpleToolbarControl AuditLogEntry-simpleToolbarControl--member"
          onPress={() =>
            this.setState({
              isMemberSelectorOpen: true,
              memberSearch: '',
            })
          }
        >
          <span className="AuditLogEntry-simpleToolbarFilterIconWrap">
            <LaunchpadIcon name="filter-list" size="small" />
            {this.state.member.trim() && <span className="AuditLogEntry-simpleToolbarFilterDot" />}
          </span>
          <span className="AuditLogEntry-simpleToolbarControlLabel">Member</span>
          {this.state.member.trim() ? (
            <span className="AuditLogEntry-simpleToolbarSelectedValueText">{selectedLabel}</span>
          ) : (
            <span className="AuditLogEntry-simpleToolbarAnyValue">any</span>
          )}
          {this.renderCompactFilterEndIcon({
            isActive: Boolean(this.state.member.trim()),
            clearLabel: 'Clear member filter',
            clearFn: this.clearMemberFilter,
          })}
        </Button>
        <Popover
          placement="bottom start"
          isOpen={this.state.isMemberSelectorOpen}
          onOpenChange={(open) => {
            this.setState({
              isMemberSelectorOpen: open,
              memberSearch: '',
            });
          }}
        >
          <Dialog aria-label="Search for audit log member">
            <div className="AuditLogEntry-memberSearchContainer">
              <div className="AuditLogEntry-memberSearchWithClear">
                <SearchField aria-label="search for audit log member" autoFocus>
                  <Group>
                    <LaunchpadIcon name="search" size="small" />
                    <Input
                      placeholder="Search by name or email..."
                      value={this.state.memberSearch}
                      onChange={(event) => this.setState({ memberSearch: event.target.value })}
                    />
                  </Group>
                </SearchField>
                {this.state.memberSearch && (
                  <Button
                    variant="minimal"
                    size="small"
                    aria-label="Clear member search"
                    onPress={() => this.setState({ memberSearch: '' })}
                  >
                    <LaunchpadIcon name="cancel" size="small" />
                  </Button>
                )}
              </div>
              <ListBox
                selectionMode="single"
                aria-label="Audit log member options"
                onSelectionChange={(keys) => {
                  const selectedKey = Array.from(keys)[0] as string;
                  if (!selectedKey || selectedKey === 'no-results' || selectedKey === 'loading-members') {
                    return;
                  }

                  if (selectedKey === 'any-member') {
                    this.setState(
                      {
                        member: '',
                        memberSearch: '',
                        isMemberSelectorOpen: false,
                      },
                      () => this.emitSearchParams(),
                    );
                    return;
                  }

                  this.setState(
                    {
                      member: selectedKey,
                      memberSearch: '',
                      isMemberSelectorOpen: false,
                    },
                    () => this.emitSearchParams(),
                  );
                }}
              >
                <ListBoxItem id="any-member" textValue="Any member">
                  <div className="AuditLogEntry-memberOption">
                    <div className="AuditLogEntry-memberOptionName">Any member</div>
                  </div>
                </ListBoxItem>
                {filteredMembers.map((member) => {
                  const displayName = getMemberDisplayName(member);
                  const showEmail = displayName !== member.email;

                  return (
                    <ListBoxItem key={member._id} id={member._id} textValue={`${displayName} ${member.email}`}>
                      <div className="AuditLogEntry-memberOption">
                        <div className="AuditLogEntry-memberOptionName">{displayName}</div>
                        {showEmail && <div className="AuditLogEntry-memberOptionEmail">{member.email}</div>}
                      </div>
                    </ListBoxItem>
                  );
                })}
                {isLoadingMembers && (
                  <ListBoxItem id="loading-members" textValue="Loading members">
                    <div className="AuditLogEntry-memberNoResults">Loading members...</div>
                  </ListBoxItem>
                )}
                {!isLoadingMembers && filteredMembers.length === 0 && this.state.memberSearch.length > 0 && (
                  <ListBoxItem id="no-results" textValue="No members found">
                    <div className="AuditLogEntry-memberNoResults">No members found</div>
                  </ListBoxItem>
                )}
              </ListBox>
            </div>
          </Dialog>
        </Popover>
      </DialogTrigger>
    );
  };

  private renderCompactDateFilter = () => (
    <Box className="AuditLogEntry-simpleToolbarDate">
      <button
        type="button"
        className="AuditLogEntry-simpleToolbarControl AuditLogEntry-simpleToolbarControl--date"
        aria-label="Date filter"
        onClick={this.openDatePicker}
      >
        <span className="AuditLogEntry-simpleToolbarFilterIconWrap">
          <LaunchpadIcon name="filter-list" size="small" />
          {this.state.date[0] && this.state.date[1] && <span className="AuditLogEntry-simpleToolbarFilterDot" />}
        </span>
        <span className="AuditLogEntry-simpleToolbarControlLabel">Date</span>
        {this.state.date[0] && this.state.date[1] ? (
          <span className="AuditLogEntry-simpleToolbarSelectedValueText">{this.getCompactDateRangeText()}</span>
        ) : (
          <span className="AuditLogEntry-simpleToolbarAnyValue">any</span>
        )}
        {this.renderCompactFilterEndIcon({
          isActive: Boolean(this.state.date[0] && this.state.date[1]),
          clearLabel: 'Clear date filter',
          clearFn: this.clearDates,
        })}
      </button>
      <ModalOverlay isOpen={this.state.isOpen} onOpenChange={this.handleDateOverlayOpenChange}>
        <Modal>
          <Dialog>
            <Heading slot="title">Select Date Range</Heading>
            <div className="AuditLogEntry-datepicker-content">
              <DateRangePicker
                onChange={this.handleDateSelection}
                allowSingleDayRange
                initialMonth={subMonths(new Date(), 1)}
                value={this.state.dateSelected}
              />
            </div>
            <div className="AuditLogEntry-datepicker-buttons">
              <button
                type="button"
                className="AuditLogEntry--datepickerbutton AuditLogEntry--datepickerbutton-cancel"
                onClick={this.cancelDateSelection}
              >
                Cancel
              </button>
              <button
                type="button"
                className="AuditLogEntry--datepickerbutton AuditLogEntry--datepickerbutton-apply"
                onClick={this.filterEntriesByDate}
              >
                Apply
              </button>
            </div>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </Box>
  );

  private getSelectedMember = () => this.props.members?.find((candidate) => candidate._id === this.state.member);

  private getMemberDisplayLabel = (member: Member) => {
    const displayName = getMemberDisplayName(member);
    return displayName === member.email ? member.email : `${displayName} (${member.email})`;
  };

  private getFilteredMembers = () => {
    const members = this.props.members ?? [];
    const query = this.state.memberSearch.trim().toLowerCase();

    if (!query) {
      return members;
    }

    return members.filter((member) => {
      const displayName = getMemberDisplayName(member).toLowerCase();
      return (
        displayName.includes(query) ||
        member.email.toLowerCase().includes(query) ||
        `${member.firstName ?? ''} ${member.lastName ?? ''}`.trim().toLowerCase().includes(query)
      );
    });
  };

  private setSearchMode = (mode: 'simple' | 'advanced') => {
    if (!this.isEnhancedSearchEnabled()) {
      return;
    }

    if (mode === this.state.searchMode) {
      return;
    }

    this.setState({ searchMode: mode }, () => this.emitSearchParams({ mode }));
  };

  private getSpecForMode = ({
    mode,
    overrideSpec,
  }: {
    mode: 'simple' | 'advanced';
    overrideSpec?: string;
  }): string | undefined => {
    if (mode === 'advanced') {
      const nextSpec = overrideSpec ?? this.state.spec;
      return nextSpec.trim() ? nextSpec : undefined;
    }

    if (overrideSpec !== undefined) {
      return overrideSpec.trim() ? overrideSpec : undefined;
    }

    if (this.state.resourceSelected === NO_RESOURCE_SELECTED) {
      return undefined;
    }

    const simpleSpec = ResourcesWrittenExpressions[this.state.resourceSelected] ?? '';
    return simpleSpec.trim() ? simpleSpec : undefined;
  };

  private emitSearchParams = ({
    overrideSpec,
    overrideDate,
    mode,
  }: {
    overrideSpec?: string;
    overrideDate?: ReadonlyArray<Date | null | number>;
    mode?: 'simple' | 'advanced';
  } = {}) => {
    if (!this.isEnhancedSearchEnabled()) {
      const trimmedQuery = this.state.query.trim();
      const nextSpec = overrideSpec ?? this.state.spec;
      const resolvedDate = overrideDate ?? (this.state.date[0] ? this.state.date : undefined);

      this.props.onSearchParamsChange({
        query: trimmedQuery || undefined,
        spec: nextSpec.trim() ? nextSpec : undefined,
        member: undefined,
        fuzzy: undefined,
        date: resolvedDate,
      });
      return;
    }

    const resolvedMode = mode ?? this.state.searchMode;
    const resolvedDate = overrideDate ?? (this.state.date[0] ? this.state.date : undefined);
    const trimmedQuery = this.state.query.trim();
    const resolvedQuery = resolvedMode === 'advanced' ? undefined : trimmedQuery || undefined;
    let resolvedFuzzy: boolean | undefined;
    if (resolvedMode === 'advanced') {
      resolvedFuzzy = false;
    } else if (trimmedQuery.length >= 2) {
      resolvedFuzzy = true;
    }

    const resolvedSpec = this.getSpecForMode({ mode: resolvedMode, overrideSpec });
    // Actions only filter when scoped to a resource (spec). Without a spec the
    // backend ignores them, so we drop them here to keep params honest.
    const resolvedActions = resolvedSpec && this.state.actions.length > 0 ? this.state.actions : undefined;

    this.props.onSearchParamsChange({
      query: resolvedQuery,
      spec: resolvedSpec,
      member: this.state.member.trim() || undefined,
      fuzzy: resolvedFuzzy,
      date: resolvedDate,
      actions: resolvedActions,
    });
  };

  private handleQueryInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    this.setState({ query: event.currentTarget.value });
  };

  private clearQuery = () => {
    this.setState({ query: '' }, () => this.emitSearchParams());
  };

  private clearMemberFilter = () => {
    this.setState({ member: '' }, () => this.emitSearchParams());
  };

  private clearResourceFilter = () => {
    if (this.state.searchMode === 'advanced') {
      this.setState({ spec: '', resourceSelected: NO_RESOURCE_SELECTED, actions: [] }, () =>
        this.emitSearchParams({ overrideSpec: '' }),
      );
      return;
    }

    this.handleResourceItemSelect(NO_RESOURCE_SELECTED);
  };

  private getCompactDateRangeText = () => {
    const [startDate, endDate] = this.state.dateSelected;
    if (startDate && endDate) {
      return `${format(startDate as Date, 'MM/dd/yy')} - ${format(endDate as Date, 'MM/dd/yy')}`;
    }
    return 'any';
  };

  private getLegacyDateRangeText = () => {
    const [startDate, endDate] = this.state.dateSelected;
    if (startDate && endDate) {
      return `Date range: ${format(startDate as Date, 'MM/dd/yyyy')} - ${format(endDate as Date, 'MM/dd/yyyy')}`;
    }
    return 'Filter by date';
  };

  private clearDates = () => {
    this.setState({ date: [null, null], dateSelected: [null, null], isOpen: false }, () =>
      this.emitSearchParams({ overrideDate: undefined }),
    );
  };

  private handleSpecInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextSpec = event.currentTarget.value;
    this.setState({ spec: nextSpec });
  };

  private handleResourceItemSelect = (item: ResourceType) => {
    const newSpec = ResourcesWrittenExpressions[item] ?? '';
    // Changing the resource invalidates any chosen actions (they are
    // resource-specific), so clear them silently.
    this.setState({ spec: newSpec, resourceSelected: item, actions: [] }, () => {
      if (!this.isEnhancedSearchEnabled() || this.state.searchMode === 'simple') {
        this.filterEntriesByQuery({ spec: newSpec });
      }
    });
  };

  private filterEntriesByQuery = (overrides?: { spec?: string }) => {
    this.emitSearchParams({ overrideSpec: overrides?.spec });
  };

  private handleDateOverlayOpenChange = (open: boolean) => {
    if (!open) {
      this.cancelDateSelection();
    }
  };

  private openDatePicker = () => {
    this.setState({
      isOpen: true,
      dateSelected: this.getDateSelectedFromDate(this.state.date),
    });
  };

  private cancelDateSelection = () => {
    this.setState({
      dateSelected: this.getDateSelectedFromDate(this.state.date),
      isOpen: false,
    });
  };

  private handleDateSelection = (selectedDates: DateRange) => {
    this.setState({
      dateSelected: selectedDates,
    });
  };

  private filterEntriesByDate = () => {
    const { dateSelected } = this.state;
    if (dateSelected[0] && dateSelected[1]) {
      this.setState(
        {
          date: [(dateSelected[0] as Date).getTime(), endOfDay(dateSelected[1] as Date).getTime()],
          isOpen: false,
        },
        () => this.emitSearchParams({ overrideDate: this.state.date }),
      );
    } else if (!dateSelected[0] && !dateSelected[1]) {
      this.setState({ date: [null, null], isOpen: false }, () => this.emitSearchParams({ overrideDate: undefined }));
    }
  };

  private getMoreEntries = () => {
    // Use the loadMore function directly - no need to go through parent state management
    if (this.props.auditLogEntries.loadMore) {
      this.props.auditLogEntries.loadMore();
    }
  };

  private getNewerEntries = async () => {
    const { loadNewer, isFetching } = this.props.auditLogEntries;
    if (!loadNewer || isFetching || this.revealingNewer) {
      return;
    }
    this.revealingNewer = true;
    this.setState({ isApplyingNewer: true, newerFailed: false });
    try {
      const anchor = this.findVisibleEntryRow();
      this.prependScrollSnapshot = {
        y: window.scrollY,
        anchorId: anchor?.id,
        anchorTop: anchor?.getBoundingClientRect().top,
      };
      const result = await loadNewer();
      if (result === 'empty') {
        this.prependScrollSnapshot = undefined;
      } else if (result === 'complete') {
        this.setState({ hideLoadNewerButton: true });
      }
    } catch {
      // Without this a failed request is indistinguishable from there being
      // nothing newer: the button simply stays put. The stale snapshot has to
      // go too, or the next render shifts the page to hold rows that never
      // arrived.
      this.prependScrollSnapshot = undefined;
      this.setState({ newerFailed: true });
    } finally {
      this.revealingNewer = false;
      this.setState({ isApplyingNewer: false });
    }
  };

  private renderNewerControls = () => {
    const { auditLogEntries, onBackToNewest } = this.props;
    if (!auditLogEntries.loadNewer || this.state.hideLoadNewerButton) {
      return null;
    }
    return (
      <div className="AuditLogEntry-loadMoreContainer">
        <Button
          variant="primary"
          className="AuditLogEntry-button"
          isDisabled={auditLogEntries.isFetching || this.state.isApplyingNewer}
          onPress={this.getNewerEntries}
        >
          Load newer
        </Button>
        {/* Always offered alongside, not only once a click has fallen short.
            Climbing to the present a batch at a time is a long way from a
            deep link, so the jump should not have to be discovered. */}
        {onBackToNewest && (
          <Button
            variant="default"
            className="AuditLogEntry-button"
            isDisabled={this.state.isApplyingNewer}
            onPress={onBackToNewest}
          >
            Back to newest
          </Button>
        )}
        {this.state.newerFailed && <div className="AuditLogEntry-loadNewerError">Could not load newer entries.</div>}
      </div>
    );
  };

  // Inserting rows would otherwise shove the open entry down the page.
  private keepPrependedRowsInPlace = (prevProps: StateProps) => {
    const snapshot = this.prependScrollSnapshot;
    if (!snapshot) {
      return;
    }
    const previousEntries = prevProps.auditLogEntries.entries;
    const nextEntries = this.props.auditLogEntries.entries;
    const previousFirst = previousEntries[0]?._id;
    const nextFirst = nextEntries[0]?._id;
    // Older pages are appended. Those updates are not the "Load newer" insert,
    // so the snapshot stays until the newer rows actually arrive.
    const appendedOnly =
      nextEntries.length >= previousEntries.length &&
      previousEntries.every((entry, index) => nextEntries[index]?._id === entry._id);
    if (!previousFirst || !nextFirst || appendedOnly) {
      return;
    }
    this.prependScrollSnapshot = undefined;
    // The user already scrolled away from the list. Don't pull them back.
    if (Math.abs(window.scrollY - snapshot.y) > 24 || snapshot.anchorTop === undefined) {
      return;
    }
    const anchor = snapshot.anchorId ? document.getElementById(snapshot.anchorId) : null;
    if (!anchor) {
      return;
    }
    const shift = anchor.getBoundingClientRect().top - snapshot.anchorTop;
    if (shift !== 0) {
      window.scrollTo({ top: window.scrollY + shift, behavior: 'auto' });
    }
  };

  private findVisibleEntryRow = (): HTMLElement | undefined => {
    const rows = document.querySelectorAll<HTMLElement>('[id^="audit-entry-"]');
    for (const row of rows) {
      const { top, bottom } = row.getBoundingClientRect();
      if (bottom > 0 && top < window.innerHeight) {
        return row;
      }
    }
    return undefined;
  };
}
